import { select } from 'd3-selection'
import { formatDefaultLocale } from 'd3-format'
import { executeForAllInsets, getFontSizeFromClass, getLegendRegionsSelector } from '../core/utils'
import { getCornerPosition, getCornerCoords } from '../core/decoration/corner-position'
import { getChoroplethLabelFormatter } from './choropleth/legend-choropleth'
import { getPropSymbolColorLabelFormatter, highlightPsSymbols, unhighlightPsSymbols } from './proportional-symbol/legend-proportional-symbols'
//types
/** @typedef {import('../types/core/MapInstance').MapInstance} MapInstance */

// Helper to check for plain objects
function isPlainObject(value) {
    return Object.prototype.toString.call(value) === '[object Object]'
}

//It performs a shallow copy — nested objects will be copied by reference, not duplicated.
//It modifies the target object (out) in place.
//Useful for merging objects or extending existing ones.
export function deepMergeExistingKeys(target, source, options = {}, seen = new WeakSet(), depth = 0) {
    const MAX_DEPTH = options.maxDepth || 100

    if (seen.has(target)) return target
    seen.add(target)

    if (depth > MAX_DEPTH) {
        console.warn(`Max recursion depth (${MAX_DEPTH}) reached.`)
        return target
    }

    for (const key in source) {
        if (source.hasOwnProperty(key) && target.hasOwnProperty(key)) {
            const sourceVal = source[key]
            const targetVal = target[key]

            // Handle functions: overwrite directly
            if (typeof sourceVal === 'function') {
                target[key] = sourceVal

                // Handle nested plain objects
            } else if (isPlainObject(sourceVal) && isPlainObject(targetVal)) {
                deepMergeExistingKeys(targetVal, sourceVal, options, seen, depth + 1)

                // Handle arrays: overwrite (or merge if option set)
            } else if (Array.isArray(sourceVal) && Array.isArray(targetVal)) {
                target[key] = options.mergeArrays ? [...new Set([...targetVal, ...sourceVal])] : [...sourceVal]

                // Handle Dates
            } else if (sourceVal instanceof Date) {
                target[key] = new Date(sourceVal.getTime())

                // Handle RegExp
            } else if (sourceVal instanceof RegExp) {
                target[key] = new RegExp(sourceVal)

                // Handle Map and Set
            } else if (sourceVal instanceof Map) {
                target[key] = new Map(sourceVal)
            } else if (sourceVal instanceof Set) {
                target[key] = new Set(sourceVal)

                // Overwrite primitives and other types
            } else {
                target[key] = sourceVal
            }
        }
    }

    return target
}

/**
 * A eurostat-map legend. This is an abstract method.
 */
export const legend = function (map) {
    //build legend object
    const out = {}

    //link map to legend
    if (map.map) {
        out.layer = map
        out.map = map.map
    } else {
        out.layer = map
        out.map = map
    }

    //the SVG where to make the legend
    out.svgId = 'em-legend-container-' + Math.round(10e4 * Math.random())
    out.svg = undefined
    out.lgg = undefined

    //the legend element position, in case it is embeded within the map SVG
    out.x = undefined
    out.y = undefined
    out.position = undefined

    //the legend box
    out.boxPadding = 7
    out.boxOpacity = 0.9

    // Only apply the legend box's background opacity (boxOpacity) once the map is zoomed away from
    // its initial view. Purely a box-opacity concern - unrelated to legend hover interactions
    // (e.g. hover-to-highlight), which should always work regardless of zoom state.
    out.onlyApplyOpacityWhileZoomed = false

    //legend title
    out.title = ''
    //legend subtitle
    out.subtitle = ''
    // we now use CSS instead of inline styles
    out.titleFontSize = getFontSizeFromClass('em-legend-title')
    // width
    out.width = 150
    //padding between title and body
    out.titlePadding = 5
    //the width of the legend box elements
    out.shapeWidth = 25
    //the height of the legend box elements
    out.shapeHeight = 20
    //the distance between consecutive legend shape elements
    out.shapePadding = 5
    //the separator line length for discrete legend swatches
    out.sepLineLength = out.shapeWidth

    // we now use CSS instead of inline styles
    out.labelFontSize = getFontSizeFromClass('em-legend-label')
    //the distance between the legend box elements to the corresponding text label
    out.labelOffsets = { x: 5, y: 5 }
    //labelFormatter function
    out.labelFormatter = null
    //set legend labels locale
    formatDefaultLocale({
        decimal: '.',
        thousands: ' ',
        grouping: [3],
        currency: ['', '€'],
    })

    //show no data
    out.noData = true
    //no data label text
    out.noDataText = out.map.noDataText_ || 'Data unavailable'
    //gap between legend and 'no data' legend
    out.noDataPadding = 5
    out.noDataShapeWidth = 25
    out.noDataShapeHeight = 20

    //the order of the legend elements where applicable. Set to false to invert.
    out.ascending = true
    out.decimals = undefined // number of decimals for legend labels, undefined for automatically determine based on data

    out.maxMin = true // show max/min values in legends where applicable
    out.maxMinLabels = ['', ''] // Labels for max and min if maxMin is true

    /** Build legend. */
    out.build = function () {
        //set SVG element and add main drawing group
        out.svg = select('#' + out.svgId)
        // clear previous legend(s)
        out.svg.selectAll('*').remove()
        // append new legend group
        out.lgg = out.svg.attr('class', 'em-legend')
    }

    /**
     * Update the legend element.
     * This is an abstract method to be defined for each specific legend.
     */
    out.update = function () {
        console.log('Legend update function not implemented')
        return out
    }

    out.updateContainer = function () {
        const layer = out.layer
        const container = out.lgg
        const legendSVG = out.svg
        // Remove previous content
        container.selectAll('*').remove()

        //check if provided external svgId has changed or if legend SVG is not yet created, then build legend
        const needsLegendBuild = layer && container && (!legendSVG || legendSVG.attr('id') !== layer.legend_.svgId)
        if (needsLegendBuild) {
            out.build()
        }

        out.applyPosition()
    }

    out.applyPosition = function () {
        const map = out.map
        const container = out.lgg
        if (!map || !container || !container.node()) return

        const hasManualX = out.x != null
        const hasManualY = out.y != null
        const userCornerPosition = getCornerPosition(out.position)
        // Don't fall back to a default 'top right' position for a caller-managed external
        // container (see _hasExternalSvgId in updateConfig()) — the caller positioned it
        // themselves and expects us to leave that transform alone unless they also opt into
        // x/y/position explicitly.
        const autoCornerFallback = !out._hasExternalSvgId
        const cornerPosition = userCornerPosition || (!hasManualX && !hasManualY && autoCornerFallback ? 'top right' : null)
        if (!hasManualX && !hasManualY && !cornerPosition) return

        const bbox = getLegendBBox(container, out)
        const buttonReserve = cornerPosition ? getLegendButtonReserve(map, cornerPosition) : 0
        const cornerCoords = cornerPosition ? getCornerCoords(cornerPosition, bbox, map, out.boxPadding, buttonReserve) : null

        let x = hasManualX ? out.x : cornerCoords ? cornerCoords.x : map.width() - out.width - out.boxPadding
        let y = hasManualY ? out.y : cornerCoords ? cornerCoords.y : out.boxPadding

        // Stacking logic for multiple legends sharing the same corner position
        const layout = map.legendLayout_ || 'stack'
        if (cornerPosition && layout === 'stack' && !hasManualX && !hasManualY) {
            // Find all legends of this map sharing this corner position
            const sharingLegends = []
            if (map.layers_ && Array.isArray(map.layers_)) {
                map.layers_.forEach((l) => {
                    const leg = l.legendObj_
                    if (leg && leg.lgg && !leg.lgg.empty()) {
                        const legUserPos = getCornerPosition(leg.position)
                        const legPos = legUserPos || (!(leg.x != null) && !(leg.y != null) ? 'top right' : null)
                        if (legPos === cornerPosition) {
                            sharingLegends.push(leg)
                        }
                    }
                })
            }

            const index = sharingLegends.indexOf(out)
            if (index > 0) {
                const spacing = 10
                let offset = 0
                for (let i = 0; i < index; i++) {
                    const prevLeg = sharingLegends[i]
                    const prevBBox = getLegendBBox(prevLeg.lgg, prevLeg)
                    offset += prevBBox.height + spacing
                }

                const [vertical] = cornerPosition.split(' ')
                if (vertical === 'bottom') {
                    y -= offset
                } else {
                    y += offset
                }
            }
        }

        container.attr('transform', `translate(${x},${y})`)
    }

    out.updateConfig = function () {
        const layer = out.layer
        // Update legend parameters if necessary
        if (layer.legend_) {
            // A caller-supplied svgId means the legend renders into a container the caller
            // manages themselves (created and positioned outside eurostat-map, commonly kept
            // outside the map's zoom group so it stays fixed on screen during pan/zoom). Track
            // that so applyPosition() knows not to impose its own default position on it — see
            // the _hasExternalSvgId check there.
            if (layer.legend_.svgId !== undefined) out._hasExternalSvgId = true
            deepMergeExistingKeys(out, layer.legend_)
        }
    }

    /** Draw legend background box */
    out.makeBackgroundBox = function () {
        out.lgg.append('rect').attr('id', 'em-legend-background').attr('class', 'em-legend-background')
        out.updateBackgroundBoxOpacity()
    }

    /** Keep conditional background opacity synchronized with the current zoom state. */
    out.updateBackgroundBoxOpacity = function () {
        const opacity = out.onlyApplyOpacityWhileZoomed && !out.map.__hasZoomed ? 0 : out.boxOpacity
        out.lgg?.select('#em-legend-background').style('opacity', opacity)
        return out
    }

    out.addTitle = function () {
        if (out.title) {
            const titlesContainer = out.lgg.append('g').attr('id', 'em-legend-titles')
            let cssFontSize = getFontSizeFromClass('em-legend-title')
            titlesContainer
                .append('text')
                .attr('class', 'em-legend-title')
                .attr('x', out.boxPadding)
                .attr('y', out.boxPadding + cssFontSize)
                .text(out.title)
        }
    }

    out.addSubtitle = function () {
        if (out.subtitle) {
            const titlesContainer = out.lgg.select('#em-legend-titles')
            let titleFontSize = getFontSizeFromClass('em-legend-title')
            let subtitleFontSize = getFontSizeFromClass('em-legend-subtitle')
            titlesContainer
                .append('text')
                .attr('class', 'em-legend-subtitle')
                .attr('x', out.boxPadding)
                .attr('y', out.boxPadding + titleFontSize + subtitleFontSize + 3) // 3px padding after title
                .html(out.subtitle)
        }
    }

    // get the initial y position for the legend elements within the legend box (boxPadding + titles + padding)
    out.getBaseY = function () {
        return (
            out.boxPadding +
            (out.title ? getFontSizeFromClass('em-legend-title') : 0) +
            (out.subtitle ? getFontSizeFromClass('em-legend-subtitle') : 0) +
            10
        )
    }
    out.getBaseX = function () {
        return out.boxPadding
    }

    /** Set legend box dimensions, ensuring it has suitable dimensions to fit to all legend graphic elements */
    out.setBoxDimension = function () {
        if (out.lgg.node()) {
            //get legend elements bounding box
            const bb = out.lgg.node().getBBox({ stroke: true })
            //apply to legend box dimensions
            const p = out.boxPadding
            out.svg
                .select('#em-legend-background')
                .attr('x', bb.x - p)
                .attr('y', bb.y - p)
                .attr('width', bb.width + 2 * p)
                .attr('height', bb.height + 2 * p)
            out.applyPosition()
        }
    }

    //shared 'single swatch + label' legend item, used for both the 'no data' box and
    //extra categorical classes mixed into an otherwise numeric/discrete classification
    function appendLegendSwatch(container, ecl, color, text, labelClass, highlightFunction, unhighlightFunction) {
        const map = out.map

        //append symbol & style
        container
            .append('rect')
            .attr('class', 'em-legend-rect')
            .style('fill', color)
            .attr('y', out.noDataPadding)
            .attr('width', out.noDataShapeWidth)
            .attr('height', out.noDataShapeHeight)
            .on('mouseover', function () {
                highlightFunction(map, ecl)
                if (map.insetTemplates_) {
                    executeForAllInsets(map.insetTemplates_, map.svgId, highlightFunction, ecl)
                }
            })
            .on('mouseout', function () {
                unhighlightFunction(map, ecl)
                if (map.insetTemplates_) {
                    executeForAllInsets(map.insetTemplates_, map.svgId, unhighlightFunction, ecl)
                }
            })

        //label
        container
            .append('text')
            .attr('class', labelClass)
            .attr('dy', '0.35em')
            .attr('x', out.noDataShapeWidth + 5)
            .attr('y', out.noDataShapeHeight / 2 + out.noDataPadding)
            .style('pointer-events', 'all')
            .style('cursor', 'pointer')
            .on('mouseover', function () {
                highlightFunction(map, ecl)
                if (map.insetTemplates_) {
                    executeForAllInsets(map.insetTemplates_, map.svgId, highlightFunction, ecl)
                }
            })
            .on('mouseout', function () {
                unhighlightFunction(map, ecl)
                if (map.insetTemplates_) {
                    executeForAllInsets(map.insetTemplates_, map.svgId, unhighlightFunction, ecl)
                }
            })
            .text(text)
    }

    //'no data' legend box
    out.appendNoDataLegend = function (container, noDataText, highlightFunction, unhighlightFunction) {
        appendLegendSwatch(container, 'nd', out.map.noDataFillStyle(), noDataText, 'em-legend-label em-legend-label-no-data', highlightFunction, unhighlightFunction)
    }

    //legend box for an extra categorical value mixed into an otherwise numeric/discrete
    //classification (e.g. a "no railway lines" class on a choropleth of electrification rates)
    out.appendCategoryLegend = function (container, ecl, color, text, highlightFunction, unhighlightFunction) {
        appendLegendSwatch(container, ecl, color, text, 'em-legend-label em-legend-label-category', highlightFunction, unhighlightFunction)
    }

    out.getNumberOfClasses = function (out) {
        const layer = out.layer
        const map = out.map
        const mapType = map._mapType
        const numberOfClasses =
            layer.numberOfClasses_ !== undefined ? layer.numberOfClasses_ : mapType === 'ps' ? map.psClasses_ : map.numberOfClasses_
        return numberOfClasses
    }

    /**
     * Dispatch to the correct label formatter based on map type.
     * - Choropleth: getChoroplethLabelFormatter (legend-choropleth.js)
     * - Prop symbols: getPropSymbolColorLabelFormatter (legend-proportional-symbols.js)
     * Both ultimately delegate to buildDiscreteLabelFormatter (legend-discrete.js).
     */
    out.getLabelFormatter = function (out) {
        const map = out.map
        const mapType = map._mapType
        if (mapType === 'ps') return getPropSymbolColorLabelFormatter(out)
        return getChoroplethLabelFormatter(out)
    }

    out.getClassToFillStyle = function (out) {
        const layer = out.layer
        const map = out.map
        const mapType = map._mapType
        const classToFillStyle =
            layer.classToFillStyle_ !== undefined ? layer.classToFillStyle_ : mapType === 'ps' ? map.psClassToFillStyle_ : map.classToFillStyle_
        return classToFillStyle
    }

    out.getColorClassifier = function (out) {
        const layer = out.layer
        const map = out.map
        const mapType = map._mapType
        const colorClassifier = layer.classifier_ !== undefined ? layer.classifier_ : mapType === 'ps' ? map.classifierColor_ : map.classifier_
        return colorClassifier
    }

    out.getColorStats = function (out) {
        const layer = out.layer
        const map = out.map

        if (layer.encodings_) {
            if (layer.encodings_['color']) {
                return layer.getEncodingStatData('color')
            }
            if (layer.encodings_['fill']) {
                return layer.getEncodingStatData('fill')
            }
        }

        const colorStat = map.statData('color')
        if (colorStat && colorStat.getArray && colorStat.getArray()?.length > 0) {
            return colorStat
        }

        const defaultStat = map.statData()
        if (defaultStat && defaultStat.getArray && defaultStat.getArray()?.length > 0) {
            return defaultStat
        }

        return map.statData()
    }

    out.getHighlightFunction = function (map) {
        if (map._mapType == 'ps') return highlightPsSymbols
        return highlightRegions
    }

    out.getUnHighlightFunction = function (map) {
        if (map._mapType == 'ps') return unhighlightPsSymbols
        return unhighlightRegions
    }

    function getLegendBBox(container, legend) {
        const node = container.node()
        // Prefer the background rect bounds when available: it represents the visual legend
        // box and is less sensitive to rotated child geometry artifacts.
        const backgroundNode = node?.querySelector?.('#em-legend-background')
        if (backgroundNode && typeof backgroundNode.getBBox === 'function') {
            try {
                const bgBBox = backgroundNode.getBBox({ stroke: true })
                if (Number.isFinite(bgBBox.width) && Number.isFinite(bgBBox.height) && (bgBBox.width || bgBBox.height)) return bgBBox
            } catch (e) {
                // Fall back to group bbox below.
            }
        }
        try {
            const bbox = node.getBBox({ stroke: true })
            if (Number.isFinite(bbox.width) && Number.isFinite(bbox.height) && (bbox.width || bbox.height)) return bbox
        } catch (e) {
            // Fall back below when the browser cannot provide a rendered bbox yet.
        }
        return { x: 0, y: 0, width: legend.width || 0, height: legend.height || 0 }
    }

    function getLegendButtonReserve(map, position) {
        if (!map.legendButton_ || map.legendButtonPosition_) return 0

        const buttonPosition = getCornerPosition(map.legendObj_?.position) || 'top left'
        if (buttonPosition !== position) return 0

        const svg = map.svg?.()
        const button = svg?.select?.('#em-legend-button')
        if (!button || button.empty()) return 0

        try {
            const bbox = button.node().getBBox({ stroke: true })
            return bbox.height + 8
        } catch (e) {
            return 38
        }
    }

    return out
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared region highlight / unhighlight (filter-based, works for all map types)
// ─────────────────────────────────────────────────────────────────────────────

let currentHighlight = null

/**
 * Reads the computed fill colour for a default (no-data) region from the DOM,
 * so the dimmed highlight colour always matches the map type's CSS.
 * Falls back to '#e1e1e1' if nothing is found.
 */
export function getDimmedFill(map) {
    const selector = getLegendRegionsSelector(map)
    // Find the first region that has no ecl (i.e. a background/no-input region)
    const candidate = map.svg_.selectAll(selector).selectAll('[ecl="ni"], .em-cntrg, .em-nutsrg').node()
    if (candidate) {
        const fill = window.getComputedStyle(candidate).fill
        if (fill && fill !== 'none') return fill
    }
    return '#e1e1e1'
}

export function highlightRegions(map, eclOrValue, options = {}) {
    const { tolerance = 0, continuous = false } = options && typeof options === 'object' && !Array.isArray(options) ? options : {}
    const halfTolerance = Math.max(0, +tolerance || 0) / 2
    currentHighlight = eclOrValue
    const dimmedFill = getDimmedFill(map)
    const selector = getLegendRegionsSelector(map)
    const allRegions = map.svg_.selectAll(selector).selectAll('[ecl]')

    if (allRegions.empty()) return

    // Bail out if the map hasn't finished rendering yet
    const isReady = allRegions.filter(function () {
        return !!this.getAttribute('fill___')
    })
    if (isReady.empty()) return

    // First pass: sync data-fill from fill___ for ALL regions before any fill changes
    allRegions.each(function () {
        const authoritative = this.getAttribute('fill___')
        if (authoritative) select(this).attr('data-fill', authoritative)
    })

    // Second pass: apply highlight/dim
    allRegions.each(function () {
        const sel = select(this)
        if (!sel.attr('data-fill')) return

        const ecl = sel.attr('ecl')
        if (!ecl || ecl === 'nd') {
            sel.style('fill', eclOrValue === 'nd' ? sel.attr('data-fill') : dimmedFill)
            return
        }

        const match = continuous ? +ecl >= eclOrValue - halfTolerance && +ecl <= eclOrValue + halfTolerance : ecl === String(eclOrValue)

        sel.style('fill', match ? sel.attr('data-fill') : dimmedFill)
    })
}

export function unhighlightRegions(map, eclOrValue) {
    currentHighlight = null
    const selector = getLegendRegionsSelector(map)
    map.svg_
        .selectAll(selector)
        .selectAll('[ecl]')
        .each(function () {
            const sel = select(this)
            const original = sel.attr('data-fill') || this.getAttribute('fill___')
            if (original) sel.style('fill', original)
        })
}

export function clearLegendHighlight(map) {
    currentHighlight = null
    const selector = getLegendRegionsSelector(map)
    map.svg_
        .selectAll(selector)
        .selectAll('[ecl]')
        .each(function () {
            const sel = select(this)
            const original = sel.attr('data-fill') || this.getAttribute('fill___')
            if (original) sel.style('fill', original)
        })
}
